// Build Calendar (Oct 27, 2026) — Alex's expected-build calendar, in the CRM.
//
// Greg: "Alex keeps his own expected build calendar on his phone. it helps him
// stay organized and tell the guys when they should tentatively schedule their
// builds." Then: "it would be the preferred build date essentially and provide
// a calendar list view for alex, show any openings available, address city
// zip, if a deal was within 5-10 minutes away, it effectively could get
// scheduled the same day as another and ramiro would be able to pm both."
//
// One date: the EXPECTED build date is deals.ho_preferred_build_date. The
// homeowner picks it at signing; Alex or the job's RM moves it here while the
// job waits on Robert, who books the real date (build_scheduled_date).
//
// Data: build_calendar_jobs (DEFINER). Oct 29, 2026 (Greg: "this is a resource
// for all to see. so no need to hide builds from other rm's. they just can't
// click and view unless it is theirs"): every team member opens the page and
// sees every job's homeowner, street and location. The link to the deal comes
// only on jobs the viewer can open (their own and split jobs, an RS's own RS
// jobs, every job for the roles that read every deal), and the $ only where
// the viewer could see it on the deal.
//
// Two views (Oct 27, 2026, Greg: "provide a calendar view option"): a list of
// weekdays, and a month grid (Mon–Fri; builds don't run on weekends) whose
// days open the same day detail underneath. `?view=month` keeps the choice
// across a trip to a deal and back. Greg: "this is definitely mobile view
// focused" — on a phone the grid shows names in each day, a tap opens the day
// as a bottom sheet, an open day lists the waiting jobs that could fill it,
// and a swipe changes the month. Phones open to the calendar until someone
// picks the list (remembered per device).
//
// Each waiting job shows where it is on the road to Robert's calendar and the
// hard gates still missing (buildReadiness.ts, built on the Submit button's own
// checks), and Risol's preferred 5 days' notice is a target the page keeps in
// view: a "book by" date on expected jobs, short-notice marks on open days,
// and suggestions that prefer days at least 5 out.
//
// "Within 5-10 minutes" is estimated from straight-line distance (roads ≈ 1.3×,
// ~30 mph): driveMinutes() in lib/buildRevenue.ts. It's a planning hint.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import DealLink from "../components/DealLink";
import { STAGE_LABELS } from "../components/pipeline/stageMap";
import { addDaysISO, etDate, mondayOf } from "../lib/weeklySpiffs";
import { useTabParam } from "../lib/useTabParam";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import { supabase } from "../lib/supabase";
import { fetchCurrentPlans } from "../lib/buildPaymentPlan";
import { canTrade, fetchTradesBetween, type Trade } from "../lib/trades";
import TradesView from "../components/trades/TradesView";
import { useTradeModals } from "../components/trades/useTradeModals";
import { itemsLine, workLabel } from "../components/trades/tradeUi";
import {
  NOTICE_DAYS,
  READINESS_COLS,
  bookBy,
  buildReadiness,
  firstNoticeDay,
  shortNotice,
  type Readiness,
} from "../lib/buildReadiness";
import {
  PAIR_MAX_MINUTES,
  SCOPED_STAGES,
  coversDay,
  daySquares,
  fmtDaySquares,
  fmtSquares,
  repShort,
  fetchCalendarJobs,
  fmtMoney,
  mapsHref,
  minutesBetween,
  setExpectedBuildDate,
  type CalJob,
} from "../lib/buildRevenue";

// Oct 28, 2026: the third view is the trades queue (Greg: "Maybe the new builds board").
const VIEWS = ["list", "month", "trades"] as const;
type View = (typeof VIEWS)[number];
const WEEKS_STEP = 6;
const MAX_WEEKS = 16; // build_calendar_jobs takes at most 120 days

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2026-11" → "2026-12" (n = +1) or "2026-10" (n = -1). */
function shiftMonth(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1, 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Short name for a calendar cell: last name, or the city when there's no name. */
function cellName(j: CalJob): string {
  const h = j.homeowner?.trim();
  if (!h) return j.city ?? (j.kind === "expected" ? "Expected" : "Booked");
  const words = h.split(/\s+/).map((w) => w.replace(/[^A-Za-z'-]/g, "")).filter((w) => w.length > 1);
  // County records read "LAST FIRST M"; everything else "First Last".
  const w = h === h.toUpperCase() ? words[0] : words[words.length - 1];
  return w ? w.split("-").map((p) => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p)).join("-") : h;
}

/** Date-only string → "Thu, Oct 30" without a timezone shift. */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d, 12);
  return `${DOW[dt.getDay()]}, ${MON[m - 1]} ${d}`;
}
function shortDay(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  return `${MON[m - 1]} ${d}`;
}
function isWeekday(iso: string): boolean {
  const [y, m, d] = iso.split("-").map(Number);
  const w = new Date(y, m - 1, d, 12).getDay();
  return w >= 1 && w <= 5;
}
function dayOfBuild(j: CalJob, day: string): string | null {
  if (!j.day || !j.endDay || j.endDay <= j.day) return null;
  let n = 1;
  let total = 1;
  for (let x = j.day; x < j.endDay; x = addDaysISO(x, 1)) {
    total += 1;
    if (x < day) n += 1;
  }
  return `Day ${n} of ${total}`;
}
const firstName = (s: string | null) => (s ? s.split(/\s+/)[0] : "");

/** A date is penciled in but the install meeting hasn't happened: the deal is
 *  still at Install Meeting (the RM moves it on by submitting the build after
 *  the meeting). Greg, Oct 27, 2026: flag these on the calendar until then. */
const meetingPending = (j: Pick<CalJob, "kind" | "stage">) => j.kind === "expected" && j.stage === "install_meeting";
const MEETING_DETAIL =
  "This is the homeowner's preferred date, not confirmed until the RM holds the meeting and submits the build.";
const MEETING_NOTE = `Install meeting hasn't happened yet. ${MEETING_DETAIL}`;

/** The flag under a job whose install meeting hasn't happened. */
function MeetingPendingLine() {
  return (
    <div className="mt-1 rounded-md border border-dashed border-violet-300 bg-violet-50 px-2 py-1 text-[11px] text-violet-800">
      <b>🤝 Install meeting not held.</b> {MEETING_DETAIL}
    </div>
  );
}

function jobTitle(j: CalJob): string {
  if (j.homeowner) return j.homeowner;
  return j.kind === "expected" || j.kind === "needs_date" ? "Expected build" : "Booked build";
}
function place(j: CalJob): string {
  return [j.city, j.zip].filter(Boolean).join(" ") || "No address";
}

interface Pair {
  other: CalJob;
  day: string;
  minutes: number;
}

const VIEW_KEY = "bp_buildcal_view";

/** Who the job belongs to, as chips: "Jordan F." (+ the split rep). */
function Reps({ j }: { j: Pick<CalJob, "rep" | "splitRep"> }) {
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      <span className="rounded bg-slate-100 px-1.5 text-[10.5px] font-semibold text-slate-700">{repShort(j.rep)}</span>
      {j.splitRep && (
        <span className="rounded bg-slate-100 px-1.5 text-[10.5px] font-semibold text-slate-700">+ {repShort(j.splitRep)}</span>
      )}
    </span>
  );
}

/** Squares as a small label; "~" marks the county parcel estimate. */
function Sq({ j }: { j: Pick<CalJob, "squares" | "squaresSource"> }) {
  const t = fmtSquares(j);
  if (!t) return <span className="text-slate-400">sq unknown</span>;
  return (
    <span
      className={j.squaresSource === "estimate" ? "text-slate-500" : "font-semibold text-slate-700"}
      title={
        j.squaresSource === "eagleview"
          ? "EagleView squares with waste (PM sheet)"
          : j.squaresSource === "estimate"
            ? "Rough estimate from the county parcel footprint"
            : j.squaresSource === "retail"
              ? "Retail scope squares"
              : "Squares recorded on the deal"
      }
    >
      {t}
    </span>
  );
}

const TONE: Record<Readiness["tone"], string> = {
  ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
  waiting: "bg-sky-50 text-sky-800 border-sky-200",
  submit: "bg-amber-50 text-amber-800 border-amber-200",
  blocked: "bg-rose-50 text-rose-800 border-rose-200",
};
const TONE_ICON: Record<Readiness["tone"], string> = { ready: "✓", waiting: "⏳", submit: "→", blocked: "✕" };

/** Where the job is on the road to Robert's calendar, and what's missing. */
function ReadinessLine({ r }: { r: Readiness | undefined }) {
  if (!r) return null;
  return (
    <div className="mt-1.5">
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE[r.tone]}`}>
        {TONE_ICON[r.tone]} {r.headline}
      </span>
      {r.missing.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {r.missing.map((m) => (
            <span key={m} className="rounded border border-rose-200 bg-white px-1.5 py-0.5 text-[10.5px] text-rose-700">
              {m}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Risol's notice for a job with an expected date. */
function NoticeLine({ day, today, r }: { day: string; today: string | null; r: Readiness | undefined }) {
  if (!today || day < today) return null;
  if (shortNotice(day, today)) {
    const hard = r?.tone === "blocked";
    return (
      <div className={`mt-1 text-[11px] font-semibold ${hard ? "text-rose-700" : "text-amber-700"}`}>
        ⚠ Inside Risol's {NOTICE_DAYS}-day notice{hard ? " and gates still missing" : ""}. Book it now or move the date.
      </div>
    );
  }
  if (r && r.tone !== "ready" && closeToBookBy(day, today)) {
    return (
      <div className="mt-1 text-[11px] font-semibold text-amber-700">
        ⚠ Book by {dayLabel(bookBy(day))} for Risol's {NOTICE_DAYS}-day notice, and it isn't approved yet
      </div>
    );
  }
  return (
    <div className="mt-1 text-[11px] text-slate-500">
      Book by <b className="text-slate-700">{dayLabel(bookBy(day))}</b> for Risol's {NOTICE_DAYS}-day notice
    </div>
  );
}

/** The book-by date for Risol's notice is today or within the next 3 days. */
const closeToBookBy = (day: string, today: string) => bookBy(day) <= addDaysISO(today, 3);

function usePhone(): boolean {
  const q = "(max-width: 639px)";
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setPhone(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return phone;
}

export default function BuildCalendar() {
  const { member, role, isSuperUser } = useAuth();
  const tradesOk = canTrade(role, isSuperUser);
  const [weeks, setWeeks] = useState(WEEKS_STEP);
  const [today, setToday] = useState<string | null>(null);
  const [jobs, setJobs] = useState<CalJob[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // "all", "mine", or a rep's full name. In the URL so a trip to a deal keeps it.
  const [repFilter, setRepFilter] = useTabParam<string>("rep", "all");
  const [saving, setSaving] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [view, setViewParam] = useTabParam<View>("view", "list", VIEWS);
  const phone = usePhone();
  const [sheetOpen, setSheetOpen] = useState(false);
  const setView = useCallback(
    (v: View) => {
      setViewParam(v);
      try {
        localStorage.setItem(VIEW_KEY, v);
      } catch {
        /* private window */
      }
    },
    [setViewParam],
  );
  // No ?view= in the URL: use this device's last choice, else calendar on a phone.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("view")) return;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(VIEW_KEY);
    } catch {
      /* ignore */
    }
    const want = saved === "list" || saved === "month" || saved === "trades" ? saved : phone ? "month" : "list";
    if (want !== "list") setViewParam(want as View);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [month, setMonth] = useState(() => etDate(new Date()).slice(0, 7));
  const [selDay, setSelDay] = useState<string | null>(null);
  // Readiness of the waiting jobs this viewer can open; null until read (or if the read failed).
  const [ready, setReady] = useState<Record<string, Readiness> | null>(null);

  const start = useMemo(() => mondayOf(), []);
  const end = useMemo(() => addDaysISO(start, weeks * 7), [start, weeks]);
  // Month grid: the Monday on or before the 1st through the week of the last day.
  const grid = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const last = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
    return {
      from: mondayOf(new Date(`${month}-01T12:00:00`)),
      to: addDaysISO(mondayOf(new Date(`${last}T12:00:00`)), 7),
    };
  }, [month]);
  const rangeFrom = view === "month" ? grid.from : start;
  const rangeTo = view === "month" ? grid.to : end;

  const load = useCallback(async () => {
    try {
      const r = await fetchCalendarJobs(rangeFrom, rangeTo);
      setToday(r.today);
      setJobs(r.jobs);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load the calendar.");
    }
  }, [rangeFrom, rangeTo]);

  useEffect(() => {
    void load();
  }, [load]);

  // Read the gates for every waiting job the viewer can open, with the same
  // checks the Submit button uses. Best-effort: a failure hides the lines.
  useEffect(() => {
    if (!jobs) return;
    const ids = jobs
      .filter((j) => j.dealId && (j.kind === "needs_date" || j.kind === "expected") && (SCOPED_STAGES as readonly string[]).includes(j.stage))
      .map((j) => j.dealId!);
    if (ids.length === 0) {
      setReady({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows: Record<string, unknown>[] = [];
        for (let i = 0; i < ids.length; i += 150) {
          const { data, error } = await supabase.from("deals").select(READINESS_COLS).in("id", ids.slice(i, i + 150));
          if (error) throw error;
          rows.push(...((data ?? []) as unknown as Record<string, unknown>[]));
        }
        const plans = await fetchCurrentPlans(ids);
        if (cancelled) return;
        const out: Record<string, Readiness> = {};
        for (const r of rows) out[String(r.id)] = buildReadiness(r, plans[String(r.id)] ?? null);
        setReady(out);
      } catch (e) {
        console.warn("build readiness read failed", e);
        if (!cancelled) setReady(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobs]);
  // A booked or moved build changes openings — refresh on deal writes, at most
  // once every few seconds (deals is a busy table).
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reloadSoon = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void load(), 3000);
  }, [load]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useRealtimeTable("deals", reloadSoon, "UPDATE");

  // Trades (Oct 28, 2026): a sub's visits on the days shown, under the builds; "+ Trade" on a build.
  const [trades, setTrades] = useState<Trade[]>([]);
  const loadTrades = useCallback(async () => {
    if (view === "trades") return;
    try {
      setTrades(await fetchTradesBetween(rangeFrom, rangeTo));
    } catch {
      setTrades([]); // the builds still show
    }
  }, [view, rangeFrom, rangeTo]);
  useEffect(() => { void loadTrades(); }, [loadTrades]);
  useRealtimeTable("trade_items", () => { void loadTrades(); });
  const tradesOn = useCallback((day: string) => trades.filter((t) => t.scheduled_date === day && t.status !== "cancelled"), [trades]);
  const tradeModals = useTradeModals(today ?? "", () => void loadTrades());
  // Close the phone's day sheet first (review, Oct 29): it sits at z-[70] and the trade sheet at z-50, so opened
  // from inside it the trade sheet sat underneath and took no taps. On a laptop the day sheet never opens.
  const onAddTrade = tradesOk
    ? (j: CalJob) => {
        if (!j.dealId) return;
        setSheetOpen(false);
        tradeModals.openNew(j.dealId, j.kind === "built" ? "new_install" : "detach_reset");
      }
    : undefined;

  const visible = useMemo(
    () =>
      (jobs ?? []).filter((j) =>
        repFilter === "all" ? true : repFilter === "mine" ? j.mine : j.rep === repFilter || j.splitRep === repFilter,
      ),
    [jobs, repFilter],
  );

  // Jobs that have a day inside the range (booked or expected), for pairing.
  const dated = useMemo(
    () => (jobs ?? []).filter((j) => j.day && j.kind !== "built" && j.kind !== "past_due" && (!today || (j.endDay ?? j.day)! >= today)),
    [jobs, today],
  );

  /** Same-day partners within ~10 minutes, nearest first, future days only. */
  const pairsFor = useCallback(
    (j: CalJob): Pair[] => {
      const out: Pair[] = [];
      for (const o of dated) {
        if (o === j || !o.day) continue;
        if (o.dealId && o.dealId === j.dealId) continue;
        const min = minutesBetween(j, o);
        if (min == null || min > PAIR_MAX_MINUTES) continue;
        // A multi-day build already under way can still take a partner on a
        // later day of it; today itself is too late to pencil anything in.
        let day = o.day;
        if (today && day <= today) {
          const last = o.endDay && o.endDay > o.day ? o.endDay : o.day;
          day = addDaysISO(today, 1);
          if (day > last) continue;
        }
        if (!isWeekday(day)) continue;
        out.push({ other: o, day, minutes: min });
      }
      return out.sort((a, b) => a.minutes - b.minutes || a.day.localeCompare(b.day)).slice(0, 3);
    },
    [dated, today],
  );

  const repNames = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs ?? []) {
      if (j.rep) set.add(j.rep);
      if (j.splitRep) set.add(j.splitRep);
    }
    if (repFilter !== "all" && repFilter !== "mine") set.add(repFilter);
    return [...set].sort((a, b) => (a === "Unassigned" ? 1 : b === "Unassigned" ? -1 : a.localeCompare(b)));
  }, [jobs, repFilter]);

  const needsDate = useMemo(
    () =>
      visible
        .filter((j) => j.dealId && (j.kind === "needs_date" || (j.kind === "expected" && today && j.day! < today)))
        .sort((a, b) => (a.day ?? "").localeCompare(b.day ?? "") || (a.city ?? "").localeCompare(b.city ?? "")),
    [visible, today],
  );
  const pastDue = useMemo(() => visible.filter((j) => j.kind === "past_due"), [visible]);
  // Jobs with no date (anyone's) that could ride along on a day.
  const undated = useMemo(() => (jobs ?? []).filter((j) => j.kind === "needs_date"), [jobs]);

  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = rangeFrom; d < rangeTo; d = addDaysISO(d, 1)) if (isWeekday(d)) out.push(d);
    return out;
  }, [rangeFrom, rangeTo]);

  const jobsOn = useCallback(
    (day: string, list: CalJob[]) =>
      list.filter(
        (j) => (j.kind === "scheduled" || j.kind === "built" || j.kind === "expected" || j.kind === "past_due") && coversDay(j, day),
      ),
    [],
  );

  const nextOpen = useMemo(() => {
    if (!today || !jobs) return null;
    const from = firstNoticeDay(today);
    return days.find((d) => d >= from && jobsOn(d, jobs).length === 0) ?? null;
  }, [days, jobs, today, jobsOn]);

  // Month view: the day whose detail shows under the grid. Defaults to today
  // when it's in the month, else the month's first weekday.
  const shownDay = useMemo(() => {
    if (view !== "month") return null;
    const inMonth = days.filter((d) => d.startsWith(month));
    if (selDay && inMonth.includes(selDay)) return selDay;
    if (today && inMonth.includes(today)) return today;
    return inMonth[0] ?? null;
  }, [view, days, month, selDay, today]);

  async function save(j: CalJob, day: string | null) {
    if (!j.dealId) return;
    setSaving(j.dealId);
    setFlash(null);
    try {
      await setExpectedBuildDate(
        j.dealId,
        day,
        member ? { id: member.id, name: member.full_name } : null,
        day ? dayLabel(day) : "",
      );
      setFlash({ ok: true, text: day ? `${jobTitle(j)} penciled in for ${dayLabel(day)}.` : `Cleared the date on ${jobTitle(j)}.` });
      await load();
    } catch (e) {
      setFlash({ ok: false, text: e instanceof Error ? e.message : "Couldn't save the date." });
    } finally {
      setSaving(null);
    }
  }

  // Group the weekdays into weeks.
  const weeksList = useMemo(() => {
    const out: Array<{ monday: string; days: string[] }> = [];
    for (const d of days) {
      const m = mondayOf(new Date(`${d}T12:00:00`));
      const last = out[out.length - 1];
      if (last && last.monday === m) last.days.push(d);
      else out.push({ monday: m, days: [d] });
    }
    return out;
  }, [days]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-5">
      <div className="flex items-start gap-3 mb-1">
        <div>
          <h1 className="text-xl font-extrabold text-bardin-ink">📅 Build Calendar</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {view === "trades"
              ? "Trades: the subs' visits (gutters, guards, soffit and fascia, detach and reset). Robert and Alex work them from here."
              : "Expected build = the preferred build date. Pencil jobs in here; Robert books the real date."}
          </p>
        </div>
        <label className={`ml-auto shrink-0 ${view === "trades" ? "hidden" : ""}`}>
          <span className="sr-only">Show jobs for</span>
          <select
            value={repFilter}
            onChange={(e) => setRepFilter(e.target.value)}
            className={`max-w-[11rem] rounded-full border px-3 py-1.5 text-[12px] font-bold ${
              repFilter === "all" ? "border-slate-200 bg-slate-50 text-slate-600" : "border-orange-300 bg-orange-50 text-orange-800"
            }`}
          >
            <option value="all">All reps</option>
            <option value="mine">My jobs</option>
            {repNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-500 mb-4">
        <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 font-bold mr-1">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={`px-2.5 py-1 rounded-full ${view === v ? "bg-white text-bardin-ink shadow-sm" : "text-slate-500"}`}
            >
              {v === "list" ? "☰ List" : v === "month" ? "▦ Calendar" : "🔧 Trades"}
            </button>
          ))}
        </div>
        {view !== "trades" && <>
        <Legend cls="bg-sky-500" label="Booked" />
        <Legend cls="bg-amber-400" label="Expected" />
        <Legend cls="bg-violet-400" label="🤝 Install meeting not held" />
        <Legend cls="bg-emerald-500" label="Open day" />
        <span>📍 = within ~{PAIR_MAX_MINUTES} min, could share a day</span>
        <span>Risol wants {NOTICE_DAYS} days' notice</span>
        <span>🔧 = a sub's visit</span>
        </>}
      </div>

      {flash && (
        <div
          className={`mb-3 rounded-lg border px-3 py-2 text-sm ${
            flash.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"
          }`}
        >
          {flash.text}
        </div>
      )}
      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</div>}
      {view === "trades" && <TradesView />}
      {view !== "trades" && !jobs && !err && <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />}

      {view !== "trades" && jobs && (
        <>
          {view === "month" && (
            <section className="mb-5">
              <MonthGrid
                month={month}
                days={days}
                today={today}
                all={jobs}
                visible={visible}
                undated={undated}
                ready={ready}
                jobsOn={jobsOn}
                tradeCount={(d) => tradesOn(d).length}
                selected={shownDay}
                onSelect={(d) => {
                  setSelDay(d);
                  if (phone) setSheetOpen(true);
                }}
                onMonth={(ym) => {
                  setMonth(ym);
                  setSelDay(null);
                }}
                onToday={() => {
                  if (today) {
                    setMonth(today.slice(0, 7));
                    setSelDay(today);
                  }
                }}
              />
              {shownDay && !phone && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white">
                  <DayRow
                    day={shownDay}
                    today={today}
                    all={jobsOn(shownDay, jobs)}
                    shown={jobsOn(shownDay, visible)}
                    undated={undated}
                    ready={ready}
                    saving={saving}
                    onSave={save}
                    trades={tradesOn(shownDay)}
                    onAddTrade={onAddTrade}
                    showFillers
                  />
                </div>
              )}
              {shownDay && phone && sheetOpen && (
                <DaySheet onClose={() => setSheetOpen(false)}>
                  <DayRow
                    day={shownDay}
                    today={today}
                    all={jobsOn(shownDay, jobs)}
                    shown={jobsOn(shownDay, visible)}
                    undated={undated}
                    ready={ready}
                    saving={saving}
                    onSave={save}
                    trades={tradesOn(shownDay)}
                    onAddTrade={onAddTrade}
                    showFillers
                  />
                </DaySheet>
              )}
              {phone && (
                <p className="mt-2 text-center text-[11px] text-slate-400">Tap a day to see it · swipe to change month</p>
              )}
            </section>
          )}

          {/* Needs a date */}
          <section className="mb-5">
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Needs an expected build date · {needsDate.length}
              {nextOpen && (
                <span className="normal-case tracking-normal font-normal text-slate-400">
                  {" "}· next open day with {NOTICE_DAYS}-day notice {dayLabel(nextOpen)}
                </span>
              )}
            </h2>
            {ready && needsDate.length > 0 && (() => {
              const t = { ready: 0, waiting: 0, submit: 0, blocked: 0 };
              for (const j of needsDate) {
                const r = ready[j.dealId!];
                if (r) t[r.tone] += 1;
              }
              const parts = [
                t.ready && `${t.ready} approved for Robert`,
                t.waiting && `${t.waiting} waiting on approval`,
                t.submit && `${t.submit} ready to submit`,
                t.blocked && `${t.blocked} missing gates`,
              ].filter(Boolean);
              return parts.length ? <p className="text-[11.5px] text-slate-600 mb-2">{parts.join(" · ")}</p> : null;
            })()}
            {needsDate.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-400 text-center">
                Every scoped job{repFilter === "mine" ? " of yours" : repFilter !== "all" ? ` of ${repShort(repFilter)}'s` : ""} has an expected date.
              </div>
            ) : (
              <div className="space-y-2">
                {needsDate.map((j) => (
                  <NeedsDateCard
                    key={j.dealId!}
                    job={j}
                    pairs={pairsFor(j)}
                    nearUndated={undated
                      .filter((o) => o !== j && (!o.dealId || o.dealId !== j.dealId))
                      .map((o) => ({ o, min: minutesBetween(j, o) }))
                      .filter((x): x is { o: CalJob; min: number } => x.min != null && x.min <= PAIR_MAX_MINUTES)
                      .sort((a, b) => a.min - b.min)
                      .slice(0, 2)}
                    nextOpen={nextOpen}
                    today={today}
                    readiness={ready?.[j.dealId!]}
                    saving={saving === j.dealId}
                    onSave={(d) => save(j, d)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Past date, not marked built */}
          {pastDue.length > 0 && (
            <section className="mb-5">
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1">
                ⚠ Past their build date, not marked built · {pastDue.length}
              </h2>
              <p className="text-[11px] text-slate-500 mb-2">
                Mark the build complete on the deal, or reschedule it. Until then it counts as scheduled revenue.
              </p>
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 divide-y divide-amber-100">
                {pastDue.map((j, i) => (
                  <div key={j.dealId ?? `p${i}`} className="px-3 py-2 flex items-start gap-2 text-sm">
                    <span className="text-[11px] text-amber-700 font-bold w-16 shrink-0 pt-0.5">{j.day ? shortDay(j.day) : "—"}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-bardin-ink truncate">
                        {j.dealId ? <DealLink id={j.dealId}>{jobTitle(j)}</DealLink> : jobTitle(j)}
                      </div>
                      <div className="text-[11.5px] text-slate-500 truncate">
                        {[j.street, place(j)].filter(Boolean).join(" · ")}
                        {j.stage ? ` · ${STAGE_LABELS[j.stage] ?? j.stage}` : ""}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px]">
                        <Reps j={j} />
                        <Sq j={j} />
                      </div>
                    </div>
                    {j.value != null && <span className="text-xs tabular-nums text-slate-500">{fmtMoney(j.value)}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* The list, week by week */}
          {view === "list" && (
          <section>
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Weekdays</h2>
            <div className="space-y-4">
              {weeksList.map((w) => {
                const booked = w.days.reduce((s, d) => s + jobsOn(d, jobs).length, 0);
                const wk = w.days.reduce(
                  (acc, d) => {
                    const ds = daySquares(jobsOn(d, jobs));
                    return { total: acc.total + ds.total, rough: acc.rough || ds.rough };
                  },
                  { total: 0, rough: false },
                );
                const open = w.days.filter((d) => (!today || d > today) && jobsOn(d, jobs).length === 0).length;
                return (
                  <div key={w.monday}>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-sm font-bold text-bardin-ink">Week of {shortDay(w.monday)}</span>
                      <span className="text-[11px] text-slate-400">
                        {booked} on the books{fmtDaySquares(wk) ? ` · ${fmtDaySquares(wk)}` : ""}
                        {open > 0 ? ` · ${open} open day${open === 1 ? "" : "s"}` : ""}
                      </span>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                      {w.days.map((d) => (
                        <DayRow
                          key={d}
                          day={d}
                          today={today}
                          all={jobsOn(d, jobs)}
                          shown={jobsOn(d, visible)}
                          undated={undated}
                          ready={ready}
                          saving={saving}
                          onSave={save}
                          trades={tradesOn(d)}
                          onAddTrade={onAddTrade}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {weeks < MAX_WEEKS && (
              <button
                type="button"
                onClick={() => setWeeks((n) => Math.min(MAX_WEEKS, n + WEEKS_STEP))}
                className="mt-4 w-full rounded-xl border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Show {Math.min(WEEKS_STEP, MAX_WEEKS - weeks)} more weeks
              </button>
            )}
          </section>
          )}
        </>
      )}
      {tradeModals.element}
    </div>
  );
}

function Legend({ cls, label }: { cls: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${cls}`} />
      {label}
    </span>
  );
}

/**
 * Expected-build-date picker. A labeled button opens a small sheet: choose a
 * date, read what saving will do, then tap "Save date". Choosing a date in the
 * sheet never saves by itself. (The bare native date input rendered as a blank
 * grey box on iPhone, and iOS fires its change event as soon as the picker
 * opens, so it picked a date on its own.)
 *
 * What a save does today (setExpectedBuildDate): sets deals.ho_preferred_build_date,
 * logs it on the job, and sends an in-app notification to the job's owner and
 * split RM when someone else made the change. It does NOT send any text message.
 * If that ever changes, add the text to SAVE_SIDE_EFFECTS below so the sheet
 * keeps telling the truth.
 */
const SAVE_SIDE_EFFECTS = {
  texts: [] as string[], // e.g. "Homeowner gets a text with the new date"
  inApp: "The job's reps get an in-app notification.",
};

function DateInput({
  value,
  disabled,
  onSave,
  min,
  today,
  who,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (d: string | null) => void;
  min?: string | null;
  today?: string | null;
  who?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const notWeekday = !!draft && !isWeekday(draft);
  const short = !!draft && !notWeekday && shortNotice(draft, today ?? null);

  function openSheet() {
    setDraft(value ?? "");
    setOpen(true);
  }
  function commit(v: string | null) {
    setOpen(false);
    onSave(v);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={openSheet}
        className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-700 ${
          disabled ? "opacity-50" : "hover:bg-slate-50"
        }`}
      >
        <span aria-hidden>📅</span>
        <span>{value ? dayLabel(value) : "Pick build date"}</span>
      </button>
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center"
            onClick={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-label="Expected build date"
              className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="font-bold text-bardin-ink">Expected build date</div>
              {who && <div className="text-xs text-slate-500">{who}</div>}
              <input
                type="date"
                aria-label="Expected build date"
                value={draft}
                min={min ?? undefined}
                onChange={(e) => setDraft(e.target.value)}
                className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              />
              {draft && !notWeekday && (
                <div className="mt-2 text-sm font-semibold text-slate-700">{dayLabel(draft)}</div>
              )}
              {notWeekday && (
                <div className="mt-2 text-sm text-rose-700">Builds run Monday to Friday. Pick a weekday.</div>
              )}
              {short && (
                <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  ⚠ That is inside Risol's {NOTICE_DAYS}-day notice. Let Robert know about the quick date. If he has
                  already OK'd it, go ahead and save.
                </div>
              )}
              <div className="mt-3 space-y-0.5 text-xs text-slate-500">
                {SAVE_SIDE_EFFECTS.texts.length > 0 ? (
                  <>
                    <div className="font-semibold text-slate-700">📱 Text messages that will be sent:</div>
                    {SAVE_SIDE_EFFECTS.texts.map((t) => (
                      <div key={t}>• {t}</div>
                    ))}
                  </>
                ) : (
                  <div>No text messages will be sent.</div>
                )}
                <div>{SAVE_SIDE_EFFECTS.inApp}</div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600"
                >
                  Cancel
                </button>
                {value && (
                  <button
                    type="button"
                    onClick={() => commit(null)}
                    className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  disabled={!draft || notWeekday || draft === value}
                  onClick={() => commit(draft)}
                  className="ml-auto rounded-lg bg-bardin-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
                >
                  Save date
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function NeedsDateCard({
  job,
  pairs,
  nearUndated,
  nextOpen,
  today,
  readiness,
  saving,
  onSave,
}: {
  job: CalJob;
  pairs: Pair[];
  nearUndated: Array<{ o: CalJob; min: number }>;
  nextOpen: string | null;
  today: string | null;
  readiness: Readiness | undefined;
  saving: boolean;
  onSave: (d: string | null) => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-bardin-ink truncate">
            <DealLink id={job.dealId!}>{jobTitle(job)}</DealLink>
          </div>
          <div className="text-[12px] text-slate-500">
            {job.street && (
              <a href={mapsHref(job)} target="_blank" rel="noreferrer" className="hover:underline">
                {job.street}
              </a>
            )}
            {job.street ? " · " : ""}
            {place(job)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
            <Reps j={job} />
            <Sq j={job} />
            <span>
              {STAGE_LABELS[job.stage] ?? job.stage}
              {job.value != null && job.value > 0 ? ` · ${fmtMoney(job.value)}` : " · no scope $ yet"}
            </span>
            {job.kind === "expected" && job.day && (
              <span className="text-amber-700">expected {shortDay(job.day)} has passed</span>
            )}
          </div>
          {meetingPending(job) && <MeetingPendingLine />}
          <ReadinessLine r={readiness} />
          {today && (
            <div className="mt-1 text-[11px] text-slate-500">
              Risol wants {NOTICE_DAYS} days' notice: pick {dayLabel(firstNoticeDay(today))} or later.
            </div>
          )}
        </div>
        {job.canEdit ? (
          <DateInput value={job.day} disabled={saving} min={today} today={today} who={jobTitle(job)} onSave={onSave} />
        ) : (
          <span className="text-[11px] text-slate-400 shrink-0">Ask the RM or Alex</span>
        )}
      </div>

      {(pairs.length > 0 || nearUndated.length > 0 || nextOpen) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {pairs.map((p, i) => (
            <button
              key={i}
              type="button"
              disabled={!job.canEdit || saving}
              onClick={() => onSave(p.day)}
              className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] text-sky-800 hover:bg-sky-100 disabled:cursor-default disabled:hover:bg-sky-50"
              title={job.canEdit ? `Pencil this job in for ${dayLabel(p.day)}` : undefined}
            >
              📍 {dayLabel(p.day)} · ~{p.minutes} min from {p.other.kind === "expected" ? "an expected" : "a booked"} build
              {p.other.city ? ` in ${p.other.city}` : ""}
              {p.other.homeowner ? ` (${firstName(p.other.homeowner)})` : ""}
              {shortNotice(p.day, today) && <span className="text-amber-700"> · short notice</span>}
              {job.canEdit && <b className="ml-0.5">· use</b>}
            </button>
          ))}
          {nearUndated.map(({ o, min }, i) => (
            <span
              key={`u${i}`}
              className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800"
            >
              📍 ~{min} min from {o.homeowner ? firstName(o.homeowner) : "another job"}
              {o.city ? ` in ${o.city}` : ""}, also waiting on a date — book them together
            </span>
          ))}
          {pairs.length === 0 && nextOpen && job.canEdit && (
            <button
              type="button"
              disabled={saving}
              onClick={() => onSave(nextOpen)}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800 hover:bg-emerald-100"
            >
              Next open day {dayLabel(nextOpen)} <b>· use</b>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Waiting (no-date) jobs within ~10 minutes of a job already on this day. Future days only. */
function ridersFor(day: string, today: string | null, all: CalJob[], undated: CalJob[]) {
  if ((today && day <= today) || all.length === 0) return [];
  return undated
    .map((u) => {
      let best: { near: CalJob; min: number } | null = null;
      for (const a of all) {
        const m = minutesBetween(u, a);
        if (m != null && m <= PAIR_MAX_MINUTES && (!best || m < best.min)) best = { near: a, min: m };
      }
      return best ? { u, ...best } : null;
    })
    .filter((x): x is { u: CalJob; near: CalJob; min: number } => !!x)
    .sort((a, b) => a.min - b.min)
    .slice(0, 3);
}

const CHIP: Record<string, string> = {
  scheduled: "bg-sky-50 border-sky-500 text-sky-900",
  expected: "bg-amber-50 border-amber-400 text-amber-900",
  built: "bg-emerald-50 border-emerald-500 text-emerald-900",
  past_due: "bg-amber-100 border-amber-600 text-amber-900",
  meeting: "bg-violet-50 border-violet-400 border-dashed text-violet-900",
};
/** The cell chip style: an expected job still waiting on its install meeting stands apart. */
const chipCls = (j: CalJob) => CHIP[meetingPending(j) ? "meeting" : j.kind] ?? "";

function MonthGrid({
  month,
  days,
  today,
  all,
  visible,
  undated,
  ready,
  jobsOn,
  tradeCount,
  selected,
  onSelect,
  onMonth,
  onToday,
}: {
  month: string;
  days: string[];
  today: string | null;
  all: CalJob[];
  visible: CalJob[];
  undated: CalJob[];
  ready: Record<string, Readiness> | null;
  jobsOn: (day: string, list: CalJob[]) => CalJob[];
  tradeCount: (day: string) => number;
  selected: string | null;
  onSelect: (d: string) => void;
  onMonth: (ym: string) => void;
  onToday: () => void;
}) {
  const [y, m] = month.split("-").map(Number);
  const inMonth = days.filter((d) => d.startsWith(month));
  const noticeFrom = today ? firstNoticeDay(today) : null;
  const open = inMonth.filter((d) => (!noticeFrom || d >= noticeFrom) && jobsOn(d, all).length === 0).length;
  const booked = inMonth.reduce((s, d) => s + jobsOn(d, all).filter((j) => j.day === d).length, 0);
  const monthSq = inMonth.reduce(
    (acc, d) => {
      const ds = daySquares(jobsOn(d, all));
      return { total: acc.total + ds.total, rough: acc.rough || ds.rough };
    },
    { total: 0, rough: false },
  );
  const noMeeting = new Set(
    inMonth.flatMap((d) => jobsOn(d, all).filter(meetingPending).map((j) => j.dealId ?? `${j.day}|${j.city}`)),
  ).size;
  const touch = useRef<{ x: number; y: number } | null>(null);
  // On an expected job: ✓ approved, Robert can book it; red ! inside Risol's
  // notice and not booked; amber ! not approved with the book-by date this week;
  // otherwise 🤝 when the install meeting hasn't happened yet.
  const mark = (j: CalJob, d: string): { t: string; cls: string } | null => {
    if (j.kind !== "expected") return null;
    const tone = j.dealId ? ready?.[j.dealId]?.tone : undefined;
    if (tone === "ready") return { t: "✓ ", cls: "text-emerald-700" };
    if (shortNotice(d, today)) return { t: "! ", cls: "text-rose-600" };
    if (tone && today && closeToBookBy(j.day ?? d, today)) return { t: "! ", cls: "text-amber-600" };
    if (meetingPending(j)) return { t: "🤝 ", cls: "" };
    return null;
  };
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-2 sm:p-3"
      onTouchStart={(e) => {
        const t = e.touches[0];
        touch.current = t ? { x: t.clientX, y: t.clientY } : null;
      }}
      onTouchEnd={(e) => {
        const s0 = touch.current;
        const t = e.changedTouches[0];
        touch.current = null;
        if (!s0 || !t) return;
        const dx = t.clientX - s0.x;
        const dy = t.clientY - s0.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) onMonth(shiftMonth(month, dx < 0 ? 1 : -1));
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => onMonth(shiftMonth(month, -1))}
          className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 font-bold"
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="min-w-0">
          <div className="text-sm font-bold text-bardin-ink">
            {MONTH_LONG[m - 1]} {y}
          </div>
          <div className="text-[11px] text-slate-400">
            {booked} build{booked === 1 ? "" : "s"} starting
            {fmtDaySquares(monthSq) ? ` · ${fmtDaySquares(monthSq)}` : ""}
            {open > 0 ? ` · ${open} open with ${NOTICE_DAYS}-day notice` : ""}
            {noMeeting > 0 && <span className="text-violet-700"> · 🤝 {noMeeting} without an install meeting</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onMonth(shiftMonth(month, 1))}
          className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 font-bold"
          aria-label="Next month"
        >
          ›
        </button>
        <button
          type="button"
          onClick={onToday}
          className="ml-auto rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
        >
          Today
        </button>
      </div>
      <div className="grid grid-cols-5 gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-1">
        {days.map((d) => {
          const own = d.startsWith(month);
          const allOn = jobsOn(d, all);
          const shown = jobsOn(d, visible);
          const past = !!today && d < today;
          const isToday = today === d;
          const isOpen = own && !past && !isToday && allOn.length === 0;
          const isShort = isOpen && shortNotice(d, today);
          const pin = own && ridersFor(d, today, allOn, undated).length > 0;
          const sel = selected === d;
          return (
            <button
              key={d}
              type="button"
              onClick={() => {
                if (!own) onMonth(d.slice(0, 7));
                onSelect(d);
              }}
              aria-pressed={sel}
              aria-label={`${dayLabel(d)}: ${allOn.length ? `${allOn.length} on the books` : isOpen ? "open" : "nothing booked"}`}
              className={`relative text-left rounded-lg border p-1 min-h-[4.5rem] sm:min-h-[6.25rem] flex flex-col overflow-hidden ${
                sel ? "ring-2 ring-orange-400 border-orange-300" : "border-slate-200"
              } ${!own ? "bg-slate-50 text-slate-300" : isShort ? "bg-slate-50 hover:bg-slate-100" : isOpen ? "bg-emerald-50 hover:bg-emerald-100" : "bg-white hover:bg-slate-50"} ${
                past && own ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-center gap-1">
                <span
                  className={`text-[11px] font-bold ${
                    isToday ? "bg-orange-500 text-white rounded-full w-5 h-5 grid place-items-center" : own ? "text-bardin-ink" : ""
                  }`}
                >
                  {Number(d.slice(8))}
                </span>
                {own && fmtDaySquares(daySquares(allOn)) && (
                  <span className="ml-auto text-[9px] sm:text-[10px] font-semibold text-slate-500 tabular-nums">
                    {fmtDaySquares(daySquares(allOn))!.replace(" sq", "")}
                    <span className="hidden sm:inline"> sq</span>
                  </span>
                )}
                {pin && (
                  <span className={`${own && daySquares(allOn).total > 0 ? "" : "ml-auto "}text-[11px]`} title="A waiting job is within ~10 minutes of this day's build">
                    📍
                  </span>
                )}
                {own && tradeCount(d) > 0 && (
                  <span className={`${own && (daySquares(allOn).total > 0 || pin) ? "" : "ml-auto "}text-[10px]`} title={`${tradeCount(d)} sub visit${tradeCount(d) === 1 ? "" : "s"} (trades)`}>
                    🔧{tradeCount(d) > 1 ? tradeCount(d) : ""}
                  </span>
                )}
              </div>
              {own && isOpen && (
                <span className={`mt-auto text-[9px] sm:text-[10px] font-bold ${isShort ? "text-slate-400" : "text-emerald-700"}`}>
                  {isShort ? "Short" : "Open"}
                  {isShort && <span className="hidden sm:inline"> notice</span>}
                </span>
              )}
              {own && shown.length > 0 && (
                <>
                  {/* Phone: two short names + the rest as a count */}
                  <div className="sm:hidden mt-0.5 flex flex-col gap-px min-w-0">
                    {shown.slice(0, 2).map((j, i) => (
                      <div
                        key={j.dealId ?? i}
                        className={`truncate rounded-sm border-l-2 pl-0.5 text-[9px] leading-3 ${chipCls(j)}`}
                      >
                        {mark(j, d) && <b className={mark(j, d)!.cls}>{mark(j, d)!.t}</b>}
                        {cellName(j)}
                      </div>
                    ))}
                    {allOn.length > Math.min(2, shown.length) && (
                      <div className="text-[9px] leading-3 text-slate-400">+{allOn.length - Math.min(2, shown.length)}</div>
                    )}
                  </div>
                  {/* Desktop: names */}
                  <div className="hidden sm:flex flex-col gap-0.5 mt-1">
                    {shown.slice(0, 3).map((j, i) => (
                      <div
                        key={j.dealId ?? i}
                        className={`truncate rounded border-l-2 px-1 text-[10.5px] leading-[14px] ${chipCls(j)}`}
                        title={`${jobTitle(j)} · ${place(j)}${j.kind === "expected" ? " · expected" : ""}${meetingPending(j) ? ` · ${MEETING_NOTE}` : ""}`}
                      >
                        {mark(j, d) && <b className={mark(j, d)!.cls}>{mark(j, d)!.t}</b>}
                        {cellName(j)}
                        {dayOfBuild(j, d) ? <span className="opacity-60"> · {dayOfBuild(j, d)!.replace("Day ", "")}</span> : null}
                        <span className="block truncate text-[9.5px] opacity-70">
                          {firstName(j.rep)}
                          {fmtSquares(j) ? ` · ${fmtSquares(j)}` : ""}
                        </span>
                      </div>
                    ))}
                    {allOn.length > Math.min(3, shown.length) && (
                      <div className="text-[10px] text-slate-400 px-1">
                        +{allOn.length - Math.min(3, shown.length)} more
                      </div>
                    )}
                  </div>
                </>
              )}
              {own && shown.length === 0 && allOn.length > 0 && (
                <span className="mt-auto text-[10px] text-slate-400">{allOn.length} other</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DaySheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", k);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Day detail">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 max-h-[78vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="sticky top-0 z-10 bg-white pt-2 pb-1 flex justify-center">
          <span className="h-1 w-10 rounded-full bg-slate-300" />
        </div>
        {children}
        <div className="px-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DayRow({
  day,
  today,
  all,
  shown,
  undated,
  saving,
  onSave,
  showFillers = false,
  ready,
  trades = [],
  onAddTrade,
}: {
  day: string;
  today: string | null;
  all: CalJob[];
  shown: CalJob[];
  undated: CalJob[];
  ready: Record<string, Readiness> | null;
  saving: string | null;
  onSave: (j: CalJob, d: string | null) => void;
  /** The calendar's day detail lists waiting jobs on an open day; the list view doesn't. */
  showFillers?: boolean;
  /** The subs' visits on this day (Oct 28, 2026). */
  trades?: Trade[];
  /** "+ Trade" on a build, for those who can add one. */
  onAddTrade?: (j: CalJob) => void;
}) {
  const past = !!today && day < today;
  const isToday = today === day;
  const n = all.length;
  const riders = ridersFor(day, today, all, undated);
  // An open day ahead: the waiting jobs this viewer can pencil in, biggest first.
  const fillers =
    !showFillers || past || isToday || n > 0 || !isWeekday(day)
      ? []
      : undated
          .filter((u) => u.canEdit && u.dealId)
          .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
          .slice(0, 6);

  const chip =
    n === 0
      ? past || isToday
        ? { cls: "bg-slate-100 text-slate-400", text: "—" }
        : shortNotice(day, today)
          ? { cls: "bg-slate-100 text-slate-600", text: "Open · short notice" }
          : { cls: "bg-emerald-100 text-emerald-800", text: "Open" }
      : { cls: "bg-sky-100 text-sky-800", text: `${n} booked${all.some((j) => j.kind === "expected") ? "/expected" : ""}` };
  const sqText = fmtDaySquares(daySquares(all));

  return (
    <div className={`px-3 py-2 ${past ? "opacity-60" : ""} ${isToday ? "bg-orange-50/50" : ""}`}>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold w-28 shrink-0 ${isToday ? "text-orange-700" : "text-bardin-ink"}`}>
          {dayLabel(day)}
        </span>
        <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${chip.cls}`}>{chip.text}</span>
        {sqText && <span className="text-[11px] font-semibold text-slate-600" title="Squares on this day (~ = includes an estimate)">{sqText}</span>}
        {shown.length < all.length && (
          <span className="text-[10.5px] text-slate-400">{all.length - shown.length} other job{all.length - shown.length === 1 ? "" : "s"}</span>
        )}
      </div>
      {shown.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {shown.map((j, i) => {
            const span = dayOfBuild(j, day);
            const expected = j.kind === "expected";
            const meeting = meetingPending(j);
            return (
              <div key={j.dealId ?? `${day}-${i}`} className="flex items-start gap-2 text-[12.5px]">
                <span className={`mt-1.5 inline-block w-2 h-2 rounded-sm shrink-0 ${meeting ? "bg-violet-400" : expected ? "bg-amber-400" : "bg-sky-500"}`} />
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-bardin-ink">
                    {j.dealId ? <DealLink id={j.dealId}>{jobTitle(j)}</DealLink> : jobTitle(j)}
                  </span>
                  {expected && <span className="ml-1 text-[10.5px] font-bold text-amber-700">EXPECTED</span>}
                  {meeting && <span className="ml-1 text-[10.5px] font-bold text-violet-700">🤝 NO INSTALL MEETING YET</span>}
                  {j.kind === "built" && <span className="ml-1 text-[10.5px] font-bold text-emerald-700">BUILT</span>}
                  {j.kind === "past_due" && <span className="ml-1 text-[10.5px] font-bold text-amber-700">NOT MARKED BUILT</span>}
                  {span && <span className="ml-1 text-[10.5px] text-slate-500">{span}</span>}
                  <div className="text-[11.5px] text-slate-500">
                    {j.street && (
                      <a href={mapsHref(j)} target="_blank" rel="noreferrer" className="hover:underline">
                        {j.street}
                      </a>
                    )}
                    {j.street ? " · " : ""}
                    {place(j)}
                    {j.value != null && j.value > 0 ? ` · ${fmtMoney(j.value)}` : ""}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px]">
                    <Reps j={j} />
                    <Sq j={j} />
                  </div>
                  {meeting && <MeetingPendingLine />}
                  {expected && j.dealId && <ReadinessLine r={ready?.[j.dealId]} />}
                  {expected && j.day && <NoticeLine day={j.day} today={today} r={j.dealId ? ready?.[j.dealId] : undefined} />}
                </div>
                {/* Stacked, so the date and + Trade take one narrow column on a phone. */}
                {((expected && j.canEdit) || onAddTrade) && j.dealId && (
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    {expected && j.canEdit && (
                      <DateInput value={j.day} disabled={saving === j.dealId} min={today} today={today} who={jobTitle(j)} onSave={(d) => onSave(j, d)} />
                    )}
                    {onAddTrade && (
                      <button type="button" onClick={() => onAddTrade(j)} title="Add a trade (gutters, guards, soffit and fascia, a detach and reset) on this job"
                              className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50">
                        + Trade
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {trades.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {trades.map((t) => (
            <div key={t.id} className="flex items-start gap-2 text-[12px]">
              <span className="pt-px">🔧</span>
              <span className="min-w-0 flex-1">
                <span className={`font-semibold ${t.needs_check ? "text-amber-800" : "text-slate-700"}`}>{workLabel(t)}</span>
                {t.deal?.homeowner_name && <> · <DealLink id={t.deal_id}>{t.deal.homeowner_name}</DealLink></>}
                <span className="text-slate-500">
                  {t.vendor ? ` · ${t.vendor}` : ""}{t.arrival_window ? ` · ${t.arrival_window}` : ""}{t.status === "complete" ? " · done" : ""}
                </span>
                {itemsLine(t) && <span className="block text-[11px] text-slate-500 truncate">{itemsLine(t)}</span>}
                {t.needs_check && <span className="block text-[11px] text-amber-700">⚠ {t.needs_check_reason}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {fillers.length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] font-bold text-emerald-800 mb-1">Open day. Waiting jobs you can pencil in:</div>
          {shortNotice(day, today) && (
            <div className="text-[11px] text-amber-700 mb-1">
              Under Risol's {NOTICE_DAYS}-day notice. Only if it has to be this soon.
            </div>
          )}
          <div className="space-y-1">
            {fillers.map((u) => (
              <div key={u.dealId!} className="flex items-center gap-2 text-[12px]">
                <span className="min-w-0 flex-1">
                  {ready?.[u.dealId!] && (
                    <span className="mr-1" title={ready[u.dealId!].headline}>
                      {TONE_ICON[ready[u.dealId!].tone]}
                    </span>
                  )}
                  <b className="text-bardin-ink">{jobTitle(u)}</b>
                  <span className="text-slate-500">
                    {" "}· {place(u)}
                    {u.value != null && u.value > 0 ? ` · ${fmtMoney(u.value)}` : ""}
                    {fmtSquares(u) ? ` · ${fmtSquares(u)}` : ""} · {repShort(u.rep)}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={saving === u.dealId}
                  onClick={() => onSave(u, day)}
                  className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-800 hover:bg-emerald-100"
                >
                  Set to this day
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {riders.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {riders.map(({ u, near, min }, i) => (
            <div key={u.dealId ?? `r${i}`} className="flex items-start gap-2 text-[11.5px] text-sky-800">
              <span className="pt-0.5">📍</span>
              <span className="min-w-0 flex-1">
                <span className="block">
                  Could add {u.homeowner ? <b>{u.homeowner}</b> : "a waiting job"}
                  {u.city ? ` in ${u.city}` : ""}
                </span>
                <span className="block text-sky-700/80">
                  ~{min} min from {near.homeowner ? firstName(near.homeowner) : "this day's build"}
                  {near.kind === "expected" ? " (expected)" : ""}
                </span>
              </span>
              {u.canEdit && u.dealId && (
                <button
                  type="button"
                  disabled={saving === u.dealId}
                  onClick={() => onSave(u, day)}
                  className="shrink-0 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-bold hover:bg-sky-100"
                >
                  Set to this day
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
